import type { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import type {
  EditorFieldType,
  FormFieldConfig,
} from '@risksmart-app/shared/forms/types';
import { supportsDefaultValue } from 'src/blocks/form-editor-dialog/config';

type AssertSubsetOf<T extends U, U> = T;

/**
 * Fails to compile if `EditorFieldType` gains a member the editor cannot offer —
 * the shape of the CUS-585 bug, where a resolved type outside the enum matched no
 * option. Asserted here because `shared` must not depend on form-configuration.
 */
export type SelectableEditorFieldType = AssertSubsetOf<
  EditorFieldType,
  `${CustomAttributeFieldType}`
>;

export const resolveStandardFieldEditorType = (
  registryField: FormFieldConfig | undefined
): EditorFieldType | '' => {
  if (!registryField) {
    return '';
  }
  if (registryField.editorFieldType) {
    return registryField.editorFieldType;
  }
  if (!registryField.displayType) {
    return 'text';
  }

  const { displayType } = registryField.displayType;

  switch (displayType) {
    case 'commonLookup':
    case 'rating':
      return 'select';
    case 'users':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'select'
        : 'usermultiselect';
    case 'departments':
      return 'departmentmultiselect';
    case 'tags':
    case 'badgeList':
      return 'multiselect' in registryField.displayType &&
        registryField.displayType.multiselect === false
        ? 'select'
        : 'multiselect';
    case 'textarea':
      return 'textarea';
    case 'date':
      return 'date';
    case 'number':
      return 'number';
    default:
      return '';
  }
};

export const standardFieldSupportsDefaultValue = (
  registryField: FormFieldConfig | undefined
): boolean =>
  supportsDefaultValue(resolveStandardFieldEditorType(registryField));

/**
 * Fields the legacy editor gives a real default via a select of options. All
 * resolve to a non-text type, so never infer defaultability from the type.
 */
export const LEGACY_DEFAULT_VALUE_FIELDS = [
  ['risk', 'Treatment'],
  ['issue', 'ImpactsCustomer'],
  ['issue', 'IsExternalIssue'],
  ['test_result', 'DesignEffectiveness'],
  ['test_result', 'PerformanceEffectiveness'],
] as const;

/** Declared non-defaultable fields only — see {@link LEGACY_DEFAULT_VALUE_FIELDS}. */
export const buildDefaultValueRefusals = (
  formConfig: Record<string, FormFieldConfig> | undefined
): ReadonlySet<string> => {
  const refused = new Set<string>();
  if (formConfig) {
    for (const field of Object.values(formConfig)) {
      if (field.editorFieldType && !standardFieldSupportsDefaultValue(field)) {
        refused.add(field.fieldId.toLowerCase());
      }
    }
  }

  return refused;
};

/** Fails open — custom fields aren't in the registry. Rows predating CUS-585 persist. */
export const isDefaultValueApplicable = (
  fieldId: string,
  refusedFieldIds: ReadonlySet<string>
): boolean => !refusedFieldIds.has(fieldId.toLowerCase());
